/* =========================================================
   The metadata-driven form control.

   Renders one field according to its declared state. Colour is
   never the only cue: a lateral bar, a state icon, a tooltip and
   an aria-label all carry it (AA). Focus in edit mode tints the
   whole row (see .field-row:focus-within).
   ========================================================= */
import { useState } from 'react';
import type { FieldDef } from '@/types';
import { sized } from '@/components/Icons';
import { ds } from '@/data';
import { COUNTRIES } from '@/data/catalogs';
import { useI18n } from '@/i18n';
import { STATE, elegidas, validateField } from '@/lib/fields';
import { estaExcusado } from '@/lib/progreso';
import { sePuedeExcusar, type EntidadNA } from '../../../db/columns';

export interface FieldRowProps {
  field: FieldDef;
  /** Texto, o lista de códigos si el campo es `multi`. */
  value: string | string[] | undefined;
  onChange?: (key: string, value: string | string[]) => void;
  editing?: boolean;
  /** La ficha entera. La necesitan las reglas que miran otro campo: «Otro»
   *  marcado obliga a escribir cuál, y ese texto vive en un campo vecino. */
  record?: Record<string, unknown> | null;
  /** Con estos dos la fila ofrece marcar «no aplica». Sin ellos no aparece:
   *  hace falta saber de qué ficha es el campo para guardar la marca. */
  entidad?: EntidadNA;
  id?: string;
  /** Se llama después de marcar o desmarcar, para que la pantalla vuelva a
   *  calcular su porcentaje. La marca no vive en el borrador, así que sin
   *  este aviso la barra se quedaría como estaba hasta el siguiente cambio. */
  onExcusar?: () => void;
}

export function FieldRow({
  field, value, onChange, editing, record, entidad, id, onExcusar,
}: FieldRowProps) {
  const { lang, t } = useI18n();
  const st = STATE[field.state];
  const label = lang === 'es' ? field.label_es : field.label_en;
  const isReadonly = field.state === 'readonly' || field.state === 'blocked' || !editing;
  const isDimmed = field.state === 'blocked';

  // «No aplica» solo se ofrece editando, sobre una ficha identificada y en
  // un campo que se pueda excusar — nunca uno rojo.
  const puedeExcusar = Boolean(editing && entidad && id && sePuedeExcusar(entidad, field.key));
  const [excusado, setExcusado] = useState(
    () => Boolean(entidad && id && estaExcusado(ds.marcasNA(), entidad, id, field.key)),
  );

  /** Marca o desmarca una opción de un campo `multi`, aplicando las dos
   *  reglas del catálogo: las fijas no se quitan, y las excluyentes vacían
   *  el resto. */
  function alternarOpcion(code: string, marcado: boolean) {
    const fijas = field.fijas ?? [];
    const excluyentes = new Set(field.excluyentes ?? []);
    const actuales = Array.isArray(value) ? value : [];

    let siguiente: string[];
    if (!marcado) {
      siguiente = actuales.filter((c) => c !== code);
    } else if (excluyentes.has(code)) {
      // «Ninguno» se lleva por delante todo lo demás
      siguiente = [code];
    } else {
      // y marcar cualquier otra cosa retira «Ninguno»
      siguiente = [...actuales.filter((c) => !excluyentes.has(c)), code];
    }
    // las fijas vuelven siempre, se haya hecho lo que se haya hecho
    for (const f of fijas) if (!siguiente.includes(f)) siguiente.push(f);

    onChange?.(field.key, [...new Set(siguiente)]);
    setTouched(true);
  }

  function alternarExcusa(marcado: boolean) {
    if (!entidad || !id) return;
    setExcusado(marcado);
    void ds.setNA(entidad, id, field.key, marcado);
    onExcusar?.();
  }

  const [touched, setTouched] = useState(false);
  const vacio = Array.isArray(value) ? !elegidas(field, value).length : !value;
  // un campo excusado no reclama: el aviso de «obligatorio y vacío» sobra
  const err = !excusado && (touched || (field.state === 'required' && vacio))
    ? validateField(field, value, record)
    : null;

  const etiqueta = (code: string) => {
    const o = field.opciones?.find((x) => x.code === code);
    return o ? (lang === 'es' ? o.label_es : o.label_en) : code;
  };

  /** La opción que abre un texto libre se enseña con ese texto al lado:
   *  «Otro (Etabs)». El texto se guarda en un campo aparte que ya no tiene
   *  fila propia —se escribe pegado a su casilla, que es donde significa
   *  algo—, así que sin esto en modo lectura no se vería en ninguna parte. */
  const conSuTexto = (code: string) => {
    const e = etiqueta(code);
    if (field.conTexto?.code !== code) return e;
    const libre = (record?.[field.conTexto.campo] ?? '').toString().trim();
    return libre ? `${e} (${libre})` : e;
  };

  let displayValue = Array.isArray(value)
    // en modo lectura una lista se enseña con sus etiquetas, no con los
    // códigos que se guardan
    ? value.map(conSuTexto).join(' · ')
    : value ?? '';
  if (field.kind === 'country' && displayValue) {
    const c = COUNTRIES.find((x) => x.code === displayValue);
    if (c) displayValue = lang === 'es' ? c.name_es : c.name_en;
  }
  if ((field.kind === 'select' || field.kind === 'siNo') && displayValue) {
    displayValue = etiqueta(displayValue);
  }

  const stateLabel = t(`field.state_${field.state}`);
  const stateTipKey = { editable: 'edit', readonly: 'read', required: 'req', blocked: 'lock' }[field.state];
  const stateTip = t(`field.state_tip_${stateTipKey}`);

  return (
    <div
      className="field-row"
      style={{
        display: 'grid', gridTemplateColumns: '4px 1fr', gap: 10,
        background: 'var(--surface)', borderRadius: 6, padding: '8px 10px 8px 0',
        opacity: isDimmed ? 0.65 : 1,
        position: 'relative',
        borderBottom: '1px solid var(--line)',
      }}
    >
      {/* lateral colour bar */}
      <div aria-hidden="true" style={{ background: st.color, borderRadius: '4px 0 0 4px', marginLeft: 0 }} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
        {/* La etiqueta se ajusta en varias líneas y NO se recorta.
            Con `truncate` las tres preguntas del perfil salían «¿LA
            ORGANIZACIÓN OFRECE ACT…» y dos de ellas quedaban idénticas en
            pantalla: el socio no podía saber cuál era la de certificaciones
            y cuál la de capacitación EDU, y contestaba a ciegas. Una
            etiqueta que no se puede leer entera no es una etiqueta. */}
        <div
          style={{
            display: 'flex', alignItems: 'flex-start', gap: 6, color: 'var(--text-2)',
            fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em',
            lineHeight: 1.35,
          }}
        >
          <span
            data-tip={`${stateLabel} — ${stateTip}`}
            style={{ color: st.color, display: 'inline-flex', alignItems: 'center', flexShrink: 0, marginTop: 1 }}
            aria-label={stateLabel}
          >
            {sized(st.icon, 11, 2)}
          </span>
          <span style={{ flex: '1 1 auto', minWidth: 0, overflowWrap: 'anywhere' }}>{label}</span>
          {/* `vacio` y no `!value`: una lista sin nada elegido sigue siendo
              un array, que es «truthy», y el punto rojo no salía nunca en
              los campos de casillas */}
          {field.state === 'required' && vacio ? (
            <span className="chip err" style={{ padding: '1px 6px', fontSize: 9 }}>•</span>
          ) : null}
          {excusado && !puedeExcusar ? (
            // fuera del modo edición la marca se ve, pero no se toca
            <span className="chip lock" style={{ padding: '1px 6px', fontSize: 9 }}>
              {lang === 'es' ? 'No aplica' : 'N/A'}
            </span>
          ) : null}
          {puedeExcusar ? (
            <label
              style={{
                marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4,
                fontSize: 10, fontWeight: 600, textTransform: 'none', letterSpacing: 0,
                color: excusado ? 'var(--text-2)' : 'var(--text-3)', cursor: 'pointer',
                flexShrink: 0, whiteSpace: 'nowrap',
              }}
              data-tip={lang === 'es'
                ? 'Este campo no aplica a tu centro. Deja de contar para tu porcentaje.'
                : 'This field does not apply to your centre. It stops counting towards your percentage.'}
            >
              <input
                type="checkbox"
                checked={excusado}
                onChange={(e) => alternarExcusa(e.target.checked)}
                style={{ margin: 0, cursor: 'pointer' }}
              />
              {lang === 'es' ? 'No aplica' : 'N/A'}
            </label>
          ) : null}
        </div>

        {isReadonly ? (
          <div
            className={field.kind === 'mono' ? 'mono' : ''}
            style={{
              color: displayValue ? 'var(--text)' : 'var(--text-3)',
              fontSize: 13.5, fontWeight: 500, padding: '3px 0', userSelect: 'text',
              // un campo excusado y vacío no es un hueco pendiente
              fontStyle: excusado && !displayValue ? 'italic' : undefined,
            }}
          >
            {displayValue || (excusado ? (lang === 'es' ? 'No aplica' : 'Not applicable') : '—')}
          </div>
        ) : field.kind === 'siNo' ? (
          // Dos opciones excluyentes, en línea. Ninguna viene marcada de
          // salida: «sin contestar» tiene que distinguirse de «no», y un
          // valor por defecto respondería por el socio.
          <div
            role="radiogroup"
            aria-label={label}
            style={{ display: 'flex', gap: 18, padding: '4px 0' }}
          >
            {field.opciones?.map((o) => (
              <label
                key={o.code}
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}
              >
                <input
                  type="radio"
                  name={`${id ?? 'x'}-${field.key}`}
                  value={o.code}
                  checked={value === o.code}
                  onChange={() => { onChange?.(field.key, o.code); setTouched(true); }}
                  style={{ margin: 0, cursor: 'pointer' }}
                />
                {lang === 'es' ? o.label_es : o.label_en}
              </label>
            ))}
          </div>
        ) : field.kind === 'select' ? (
          <select
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => { onChange?.(field.key, e.target.value); setTouched(true); }}
            aria-label={label}
            aria-invalid={err ? true : undefined}
            style={{
              border: '1px solid ' + (err ? 'var(--err)' : 'var(--line-2)'),
              background: 'var(--surface)', borderRadius: 5,
              padding: '6px 8px', fontSize: 13.5, color: 'var(--text)',
              outline: 'none', width: '100%',
            }}
          >
            <option value="">{lang === 'es' ? '-- Selecciona --' : '-- Select --'}</option>
            {field.opciones?.map((o) => (
              <option key={o.code} value={o.code}>{lang === 'es' ? o.label_es : o.label_en}</option>
            ))}
          </select>
        ) : field.kind === 'multi' ? (
          <div
            role="group"
            aria-label={label}
            style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '2px 0' }}
          >
            {field.opciones?.map((o) => {
              const marcada = Array.isArray(value) && value.includes(o.code);
              // las fijas se enseñan marcadas y apagadas: se ven, se entiende
              // que van, y no se pueden quitar
              const fija = (field.fijas ?? []).includes(o.code);
              return (
                <label
                  key={o.code}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 7, fontSize: 13,
                    cursor: fija ? 'default' : 'pointer',
                    color: fija ? 'var(--text-3)' : 'var(--text)',
                  }}
                  data-tip={fija
                    ? (lang === 'es'
                        ? 'Va en todos los centros y no se puede quitar.'
                        : 'Applies to every centre and cannot be removed.')
                    : undefined}
                >
                  <input
                    type="checkbox"
                    checked={marcada || fija}
                    disabled={fija}
                    onChange={(e) => alternarOpcion(o.code, e.target.checked)}
                    style={{ margin: 0, cursor: fija ? 'default' : 'pointer' }}
                  />
                  {lang === 'es' ? o.label_es : o.label_en}
                  {/* «Otro» abre su texto aquí mismo: separarlo en una fila
                      aparte obligaba a buscarlo, y se quedaba vacío */}
                  {field.conTexto?.code === o.code && marcada ? (
                    <input
                      type="text"
                      value={(record?.[field.conTexto!.campo] ?? '').toString()}
                      onChange={(e) => onChange?.(field.conTexto!.campo, e.target.value)}
                      placeholder={lang === 'es' ? '¿Cuál?' : 'Which one?'}
                      aria-label={lang === 'es' ? 'Otro producto, ¿cuál?' : 'Other product, which one?'}
                      style={{
                        marginLeft: 6, flex: 1, minWidth: 0,
                        border: '1px solid var(--line-2)', background: 'var(--surface)',
                        borderRadius: 5, padding: '3px 7px', fontSize: 12.5, outline: 'none',
                      }}
                    />
                  ) : null}
                </label>
              );
            })}
          </div>
        ) : (
          <input
            type={field.kind === 'number' ? 'number' : 'text'}
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange?.(field.key, e.target.value)}
            onBlur={() => setTouched(true)}
            aria-label={label}
            aria-invalid={err ? true : undefined}
            className={field.kind === 'mono' ? 'mono' : ''}
            style={{
              border: '1px solid ' + (err ? 'var(--err)' : 'var(--line-2)'),
              background: 'var(--surface)',
              borderRadius: 5,
              padding: '6px 8px', fontSize: 13.5,
              color: 'var(--text)', outline: 'none',
              width: '100%',
            }}
          />
        )}
        {err ? <div style={{ color: 'var(--err)', fontSize: 11, fontWeight: 500 }}>{t(`field.${err}`)}</div> : null}
      </div>
    </div>
  );
}
